<template>

  <!-- Loader -->
  <LoadingDiv v-if="!isReady" />

  <div class="auth-start-shell sba-slide-in" v-else>
    <template v-if="manualAuthEnabled">
      <form action="#">
      <div class="auth-start-card">
      <div class="auth-start-main" :class="{ 'has-error': error }">
        <div class="auth-start-copy">
          <span>Step 1</span>
          <h6>Enrollee Details</h6>
          <p>Enter the enrollee identity exactly as it should appear on the authorization request.</p>
        </div>
        <div class="auth-start-fields">
          <div class="auth-start-field">
            <div class="input-field auth-input-field">
              <input type="text" id="enrolleeId" v-model="enrolleeId" autocomplete="off" />
              <label for="enrolleeId">Enrollee ID</label>
            </div>
          </div>
          <div class="auth-start-field">
            <div class="input-field auth-input-field">
              <input type="text" id="enrolleeName" v-model="enrolleeName" autocomplete="off" />
              <label for="enrolleeName">Enrollee Name</label>
            </div>
          </div>
        </div>
        <div class="auth-start-error" v-if="error">
          <i class="material-icons">error_outline</i>
          <span>{{ error }}</span>
        </div>
      </div>
     
      <div class="auth-scheme-panel">
        <div class="auth-scheme-title">
          <span>Step 2</span>
          <h6>Scheme</h6>
        </div>
        <div class="auth-scheme-grid">
          <label class="auth-scheme-option" :class="{ 'is-selected': selectedScheme?.id === scheme.id }" v-for="scheme in schemes" :key="scheme.id">
            <input id="selectedScheme" v-model="selectedScheme" :value="scheme" type="radio" />
            <span>
              <strong>{{ scheme.name }}</strong>
              <small>{{ scheme.type == 'p' ? 'Private Scheme' : 'Social Scheme' }}</small>
            </span>
          </label>
        </div>
      </div>

      <div class="auth-start-submit">
        <button type="button" class="auth-next-button" :class="{
          disabled: !enrolleeId || !enrolleeName || !selectedScheme,
        }" @click="handleSearch()">
          <span>Next</span>
          <i class="material-icons" v-if="!isLoading">chevron_right</i>
        </button>
      </div>
      </div>
      </form>
    </template>

    <template v-else>
      <div class="auth-start-search-card">
        <form class="auth-search-form" @submit.prevent="handleSearch()">
          <div class="auth-search-main" :class="{ 'has-error': error }">
            <div class="auth-start-copy">
              <span>Step 1</span>
              <h6>Find Enrollee</h6>
              <p>Search the active enrollee register before creating a new authorization request.</p>
            </div>
            <div class="auth-search-control">
              <div class="input-field auth-input-field">
                <input type="search" id="authEnrolleeSearch" v-model="enrolleeId" autocomplete="off" />
                <label for="authEnrolleeSearch">Enrollee ID</label>
              </div>
            </div>
            <div class="auth-start-error" v-if="error">
              <i class="material-icons">error_outline</i>
              <span>{{ error }}</span>
            </div>
          </div>

          <div class="auth-start-submit">
            <button type="button" class="auth-clear-button" :class="{ disabled: isLoading || (!enrolleeId && !enrolleeGroup?.length) }"
              @click.prevent="handleClear">
              <i class="material-icons">restart_alt</i>
              <span>Clear</span>
            </button>
            <button type="submit" class="auth-next-button" :class="{ disabled: isLoading || !enrolleeId }">
              <span>{{ isLoading ? "Searching..." : "Search Register" }}</span>
              <i class="material-icons" v-if="!isLoading">search</i>
            </button>
          </div>
        </form>

        <div class="progress auth-start-progress" v-if="isLoading">
          <div class="indeterminate"></div>
        </div>

        <div class="auth-search-note" v-if="!enrolleeGroup?.length && !error && !isLoading">
          <i class="material-icons">verified_user</i>
          <span>Search and select an active enrollee from the register to continue.</span>
        </div>

        <!-- Confirmation for more than multiple enrollees -->
        <div class="auth-start-results" v-if="enrolleeGroup?.length">
          <button type="button" class="auth-start-result-card" v-for="enrollee in enrolleeGroup" :key="enrollee.id"
            @click.prevent="handleStart(enrollee)">
            <div class="auth-result-main">
              <span>{{ enrollee?.scheme.name.toUpperCase() }}</span>
              <h6>{{ capWords(enrollee?.name) }}</h6>
              <p>{{ enrollee?.enrollee_number }}</p>
            </div>
            <i class="material-icons">chevron_right</i>
          </button>
        </div>
      </div>
    </template>

  </div>

</template>

<script>
import { ref, watchEffect, onMounted } from "vue";
import { useStore } from "vuex";
import { capWordsFx } from "@/composables/fxLibrary/coreFx";
import getItems from "@/composables/getItems";
import getItemsOfItems from "@/composables/getItemsOfItems";
import { getCachedServerDate } from "@/composables/requestOptimization";
import { normalizeEnrolleeNumber, normalizeEnrolleeSearchValue } from "@/composables/enrolleeNumber";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { ID_NUMBER, addManualAuth } from '@/laravel/config';

export default {
  name: "StartAuthComponent",
  components: { LoadingDiv },
  emits: ["next"],
  setup(props, context) {
    const manualAuthEnabled = addManualAuth;
    const response = ref(null);
    const enrolleeGroup = ref(null);
    const enrolleeId = ref(null);
    const enrolleeName = ref(null);
    const selectedScheme = ref(null);
    const serverDate = ref(null);
    const error = ref(false);
    const isReady = ref(!manualAuthEnabled);
    const isLoading = ref(false);

    // store
    const store = useStore();


    // display
    onMounted(() => {
      // get standalone server date
      if (manualAuthEnabled) {
        (async () => {
        const { res } = await getCachedServerDate(getItems);

        if (res.value) {
          serverDate.value = res.value.data.current_date_full;
        }
        // open page
        isReady.value = true;
      })();

      }
      
    });


    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // get function
    const getSearchItemsFromApi = (table, match) => {
      // loading starts
      isLoading.value = true;
      // clear error
      error.value = null;
      enrolleeGroup.value = [];
      response.value = null;

      (async () => {
        const { res } = await getItemsOfItems(table, match);

        if (res.value && res.value.status && res.value.data?.length) {
          // set server date
          serverDate.value = res.value.server;
          // display options
          if (res.value.data.length === 1) {
            response.value = res.value.data[0];
          } else {
            enrolleeGroup.value = res.value.data;
          }
        } else {
          error.value = `Enrollee not on current List!`;
          response.value = null; // clear standing response
        }

        // page is ready, loading is complete
        isLoading.value = false;
      })();
    };

    // search
    const handleSearch = () => {

      if (!manualAuthEnabled) {
        if (!enrolleeId.value?.trim()) {
          error.value = "Enter an enrollee ID to search.";
          return;
        }

        // loading starts
        isLoading.value = true;
        // val
        let val = `q=${encodeURIComponent(normalizeEnrolleeSearchValue(enrolleeId.value))}`;
        // search: 0 = provider_id
        getSearchItemsFromApi("operator/enrollee/encounter/checker", `0?${val}`);


      } else {
        if (!enrolleeId.value || !enrolleeName.value || !selectedScheme.value) {
          error.value = "Enter enrollee ID, enrollee name, and scheme.";
          return;
        }

        // build pseudo enrollee
        let newVal = {
          id: Date.now(),
          payer_id: ID_NUMBER,
          name: enrolleeName.value,
          enrollee_number: normalizeEnrolleeNumber(enrolleeId.value),
          dob: null,
          sex: null,
          client_name: null,
          scheme: selectedScheme.value,
          scheme_id: selectedScheme.value?.id,
          profile_img: null,
        };

        // push start
        handleStart(newVal);

      }

    };

    // start
    const handleStart = (val) => {
      // set
      response.value = val;
    }

    // clear
    const handleClear = () => {
      enrolleeGroup.value = [];
      enrolleeId.value = null;
      enrolleeName.value = null;
      response.value = null;
      error.value = false;
    };

    // next
    watchEffect(() => {
      if (response.value) {
        context.emit("next", {
          value: response.value,
          serverDate: serverDate.value,
        });
      }
    });

    return {
      capWords,
      handleStart,
      handleSearch,
      handleClear,
      error,
      isReady,
      isLoading,
      enrolleeGroup,
      enrolleeId,
      enrolleeName,
      selectedScheme,
      manualAuthEnabled,
      schemes: store.state.user.schemes
    };
  },
};
</script>

<style scoped>
.auth-start-shell {
  margin-top: 16px;
}

.auth-start-card,
.auth-start-search-card,
.auth-start-result-card {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.auth-start-card,
.auth-start-search-card {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: none;
  overflow: hidden;
}

.auth-start-main,
.auth-search-main {
  background: #ffffff;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(220px, 0.42fr) minmax(0, 1fr);
  padding: 24px;
}

.auth-start-main.has-error,
.auth-search-main.has-error {
  background: #fff8f8;
}

.auth-start-copy span,
.auth-scheme-title span {
  color: #009688;
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.auth-start-copy h6,
.auth-scheme-title h6 {
  color: #263238;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.auth-start-copy p {
  color: #607d8b;
  line-height: 1.45;
  margin: 8px 0 0;
}

.auth-start-fields {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(180px, 0.72fr) minmax(240px, 1fr);
}

.auth-start-field,
.auth-search-control {
  background: #f8fafb;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  min-width: 0;
  padding: 14px 12px 0;
}

.auth-input-field {
  margin: 0;
}

.auth-input-field input {
  border-bottom-color: transparent !important;
  color: #263238;
  font-weight: 700;
  margin-bottom: 0 !important;
}

.auth-input-field input:focus {
  border-bottom-color: #00897b !important;
  box-shadow: 0 1px 0 0 #00897b !important;
}

.auth-start-error {
  align-items: center;
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  color: #b71c1c;
  display: flex;
  font-weight: 700;
  gap: 8px;
  grid-column: 2;
  padding: 10px 12px;
}

.auth-start-error i {
  font-size: 18px;
}

.auth-scheme-panel {
  background: #f8fafb;
  border-top: 1px solid #e1e8eb;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(220px, 0.42fr) minmax(0, 1fr);
  padding: 20px 24px;
}

.auth-scheme-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.auth-scheme-option {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid #dfe7ea;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  min-height: 74px;
  padding: 14px;
}

.auth-scheme-option.is-selected {
  background: #e8f3f2;
  border-color: #00897b;
}

.auth-scheme-option input {
  margin-top: 4px;
}

.auth-scheme-option span {
  color: #263238;
  line-height: 1.3;
}

.auth-scheme-option strong {
  display: block;
  font-weight: 700;
}

.auth-scheme-option small {
  color: #607d8b;
  display: block;
  font-weight: 700;
  margin-top: 5px;
}

.auth-start-submit {
  align-items: center;
  background: #f5f5f5;
  border-top: 1px solid #e1e8eb;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 16px 24px;
}

.auth-next-button,
.auth-clear-button {
  align-items: center;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 8px;
  min-height: 42px;
  padding: 0 16px;
}

.auth-next-button {
  background: #263238;
  border: 1px solid #263238;
  color: #ffffff;
}

.auth-clear-button {
  background: #ffffff;
  border: 1px solid #cfd8dc;
  color: #455a64;
}

.auth-next-button i {
  font-size: 20px;
}

.auth-clear-button i {
  color: #00897b;
  font-size: 19px;
}

.auth-next-button.disabled,
.auth-clear-button.disabled {
  background: #cfd8dc;
  border-color: #cfd8dc;
  color: #607d8b;
  pointer-events: none;
}

.auth-start-progress {
  border-radius: 0;
  height: 3px;
  margin: 0;
}

.auth-search-note {
  align-items: center;
  background: #f8fafb;
  border-top: 1px solid #e1e8eb;
  color: #607d8b;
  display: flex;
  gap: 10px;
  line-height: 1.4;
  padding: 14px 24px;
}

.auth-search-note i {
  color: #00897b;
  font-size: 20px;
}

.auth-start-results {
  background: #fafafa;
  border-top: 1px solid #e1e8eb;
  display: grid;
  gap: 10px;
  padding: 14px;
}

.auth-start-result-card {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dbe5e9;
  border-radius: 6px;
  box-shadow: none;
  color: #263238;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
  text-align: left;
  transition: border-color 160ms ease, transform 160ms ease;
}

.auth-start-result-card:hover {
  border-color: #00897b;
  transform: translateY(-1px);
}

.auth-result-main {
  min-width: 0;
}

.auth-result-main span {
  color: #00897b;
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 7px;
  text-transform: uppercase;
}

.auth-result-main h6 {
  color: #263238;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  overflow-wrap: anywhere;
}

.auth-result-main p {
  color: #607d8b;
  font-weight: 700;
  margin: 5px 0 0;
}

.auth-start-result-card > i {
  background: #eef3f5;
  border-radius: 6px;
  color: #263238;
  flex: 0 0 auto;
  padding: 8px;
}

@media only screen and (max-width: 992px) {
  .auth-start-main,
  .auth-search-main,
  .auth-scheme-panel,
  .auth-start-fields {
    grid-template-columns: 1fr;
  }

  .auth-start-error {
    grid-column: auto;
  }

  .auth-start-submit {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .auth-next-button,
  .auth-clear-button {
    justify-content: center;
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-start-card,
  .auth-start-search-card,
  .auth-start-result-card {
    animation: none;
  }
}
</style>
